<template>
  <div class="product-gallery select-none px-6 py-8 lg:p-0">
    <div class="flex relative pr-0 lg:pr-[2.14286rem] pt-8 lg:pt-12 pb-9 lg:pb-12">
      <div
        class="cursor-pointer absolute top-1/3 left-[0.25rem] lg:left-[5%] pl-0 lg:pl-8 z-10"
        :style="hideLeftArrow"
        role="button"
        tabindex="0"
        aria-label="Previous image"
        @click="navigateLeft"
        @keyup.enter="navigateLeft"
        @keyup.space="navigateLeft"
        id="previous-button"
      >
        <Icon icon="arrow-left" class="text-[1.3rem] hover:text-gold-100" />
      </div>
      <div
        class=" cursor-pointer absolute top-1/3 right-[0.25rem] lg:right-[5%] pr-0 lg:pr-8 z-10"
        :style="hideRightArrow"
        role="button"
        tabindex="0"
        aria-label="Next image"
        @click="navigateRight"
        @keyup.enter="navigateRight"
        @keyup.space="navigateRight"
         id="next-button"
      >
        <Icon icon="arrow-right" class="text-[1.3rem] hover:text-gold-100" />
      </div>
      <div class="thumbnails absolute bottom-0 gap-2 justify-around w-full lg:width-[3.429rem] flex lg:justify-center lg:px-0 lg:relative lg:pb-0 lg:mt-0 lg:block lg:w-auto">
        <div
          v-if="productImages.length"
          v-for="(imageUrl, index) in productImages"
          :key="index"
          class="thumbnail lg:mb-[0.714286rem] w-full m-h-full flex items-center lg:w-auto lg:h-auto max-h-[3.9285714286rem] max-w-[3.5714285714rem] rounded-[0.428571rem] overflow-hidden width-[3.286rem] border hover:border-ash-600"
          :class="[index === activeImageIndex ? 'border-ash-100' : 'border-ash-300', 'rounded-[0.4286rem] cursor-pointer']"
          @click="activeImageIndex = index;showTranscript=false"
          @keyup.enter="activeImageIndex = index;showTranscript=false"
          tabindex="0"
          role="button"
        >
          <div :aria-label="`${productName} thumbnail youtube video ${index + 1}`" class="youtube h-auto w-full flex justify-center items-center !bg-cover lg:w-[3.5714286rem] lg:h-[3.5714286rem] relative" v-if="isYouTubeId(imageUrl)" :style="{ background: `url('https://i3.ytimg.com/vi/${imageUrl}/maxresdefault.jpg')` }" :id="`thumbnail-youtube-${index}`">
            <Icon icon="play" class="text-[1.5rem] text-black-100"/>
          </div>
          <video 
            :aria-label="`${productName} thumbnail video ${index + 1}`"
            :title="`Video content for ${productName}`"
            :poster="primaryImageUrl" 
            v-else-if="isVideo(activeImage)"
            :id="`thumbnail-video-${index}`">
            <source :src="activeImage" type="video/mp4" />
          </video>
          <NuxtImg :src="imageUrl" :alt="`${productName} thumbnail ${index + 1}`" :title="`${productName} thumbnail image ${index + 1}`" :id="`thumbnail-image-${index}`" v-else />
        </div>
      </div>
      <div id="active-img" class="active-image flex justify-center w-full lg:w-[71%] mx-auto pb-6 lg:pb-0" @touchstart="swipeHandler" @touchend="swipeHandler">
        <div v-if="isYouTubeId(activeImage)" class="w-full">
          <YoutubeVideo :video-id="activeImage" class="w-full" id="product-youtube"/>
          <div class="flex mt-8" v-if="getVideoId(activeImage)" >
            <SocialShareButton
              v-for="btn in buttons"
              key="youtubeWatch"
              network="youtubeWatch"
              :videoId="getVideoId(activeImage)"
              class="mr-2 text-[1rem]"
              aria-label="Audio description opens in a new tab"
              role="link"
            >
              <Icon :icon="btn.icon" class="text-white text-[1rem]" />
              <span class="font-normal">{{ btn.label }}</span>
            </SocialShareButton>
            <Button 
              @click="toggleTranscript(activeImage)" 
              v-if="transcriptMapped(activeImage)" 
               :class="['flex text-[1rem] bg-ash-500 items-center gap-[0.4642857rem] cursor-pointer font-medium !rounded-[0.4285rem] lg:pl-[0.714rem] lg:pr-[1rem] pt-[0.4285714rem] pb-[0.5rem] border-gray-900 border hover:bg-gray-400',
              { '!bg-ash-800': showTranscript }]"
              variant="primary"
              theme="LIGHT"
              size="md"
              type="button"
              aria-label="Video Transcript"
              title="Video Transcript"
              id="video-transcript">
                <Icon icon="quote-left" class="text-white text-[1rem]" />
                <span class="font-normal">Video Transcript</span>
            </Button>
          </div>
        </div>
        <video controls v-else-if="isVideo(activeImage)" :aria-label="`Video content for ${productName}`" :title="`Video content for ${productName}`" :poster="primaryImageUrl" id="product-video">
          <source :src="activeImage" type="video/mp4" />
        </video>
        <NuxtImg v-else :src="activeImage" :alt="`Large image of ${productName}`" :title="`Large image of ${productName}`" class="w-[40.29rem]" id="product-image"/>
        <div class="sr-only" aria-live="polite">
          {{
            isYouTubeId(activeImage) ? `Current media: YouTube video for ${productName} and thumbnail ${activeImageIndex + 1} `: isVideo(activeImage) ? `Current media: Video content for ${productName} and thumbnail ${activeImageIndex + 1}`: `Current image for thumbnail ${activeImageIndex + 1}`
          }}
        </div>
      </div>
    </div>
    <Transcript
      v-if="showTranscript"
      :heading="heading"
      :description="description"
      :showMoreBtn="false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import Button from "../../atom/button.vue"
import Icon from "../../atom/icon.vue"
import YoutubeVideo from '../../organism/youtubeVideo.vue'
import Transcript from '../../molecule/transcript.vue'
import SocialShareButton from '../../atom/SocialShareButton.vue'
import type { ButtonConfig } from '~~/shared/types/common'

const props = defineProps<{
  product: string[]
  externalMediaIds: string[]
  audioDescriptions: Record<string, { videoId: string; transcription: string }>
  productName?: string
  primaryImageUrl?: string
  productId?:number
}>()

const activeImageIndex = ref(0)
const touchStart = ref(0)
const touchEnd = ref(0)

const productImages = computed(() => {
  const images = [...props.product, ...props.externalMediaIds] 
  if (images.length === 0 && props.primaryImageUrl) 
  { 
    return [props.primaryImageUrl] 
  }
  return images
})

const navigateLeft = () => {
  if (activeImageIndex.value > 0) activeImageIndex.value--
}

const navigateRight = () => {
  if (activeImageIndex.value < productImages.value.length - 1) activeImageIndex.value++
}

const activeImage = computed(() => productImages.value[activeImageIndex.value])

const isYouTubeId = (id) => id && /^[0-9a-zA-Z\-_]{11}$/.test(id);
const isVideo = (url) => /mp4$/.test(url) || /video\/mp4/.test(url);

const hideLeftArrow = computed(() =>
  activeImageIndex.value === 0 ? 'visibility:hidden' : ''
)

const hideRightArrow = computed(() =>
  activeImageIndex.value === productImages.value.length - 1 ? 'visibility:hidden' : ''
)

const buttons: ButtonConfig[] = [
  {
    network: 'youtubeWatch',
    icon: 'universal-access',
    label: 'Audio Description',
    videoId: null,
    title: null,
    description: null
  }
] as const

const showTranscript = ref(false) 
const heading = ref("Transcript");
const description = ref("");

const getDetails = (activeImage) => {
  return props.audioDescriptions?.[activeImage] || null
}

const toggleTranscript = (activeImage) => {
  const details = getDetails(activeImage)
  if (details?.transcription) {
    showTranscript.value = !showTranscript.value
    description.value = details.transcription
  }
}

const getVideoId = (activeImage) => getDetails(activeImage)?.videoId || null

const transcriptMapped = (activeImage) => getDetails(activeImage)?.transcription || null

const swipeHandler = (event) => {
    if (event.type === 'touchend') {
      touchEnd.value = event.changedTouches[0].screenX
      const delta = touchStart.value - touchEnd.value
      if (Math.abs(delta) > 50) {
        if (delta > 0) navigateRight()
        else navigateLeft()
      }
    } else if (event.type === 'touchstart') {
      touchStart.value = event.changedTouches[0].screenX
    }
}
</script>
